import React, { useState } from "react";

import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { CardLabel } from "@src/components/card_label";
import {
  NOTIFICATION_TYPE,
  PHASE_NAME_MAPPING,
  PROJECT_PHASE,
} from "@src/constants";
import { DigixPhase, DigixProject } from "@src/types/project_types";
import { Button, Card, Col, Collapse, Flex, Row } from "antd";
import { CollapseProps } from "antd/lib";

import { DigiXPhase } from "../digix_phase";

import "./digix_details_tab.less";
import { NotificationBanner } from "@src/components/notification_banner";
import { RequestOverviewDetails } from "@src/types";

interface DigiXDetailsProps {
  digiXProject: DigixProject;
  request: RequestOverviewDetails;
}

const arrowPhaseText: string[] = [
  "One Stage Option",
  "One Stage Option",
  "Implementation",
  "Construction",
  "Operation",
];

const notAvailableText = "N.A.";

const DigiXDetailsTab: React.FC<DigiXDetailsProps> = ({
  digiXProject,
  request,
}) => {
  const [activeKeys, setActiveKeys] = useState<string[]>(["0"]);
  const [allCollapsed, setAllCollapsed] = useState<boolean>(true);

  const items: CollapseProps["items"] = digiXProject?.phases?.map(
    (phase: DigixPhase, index: number) => {
      return {
        key: `${index}`,
        label: `${phase.phaseTypeId}. ${PROJECT_PHASE[phase.phaseTypeId - 1]}`,
        children: (
          <DigiXPhase
            phase={phase}
            arrowText={arrowPhaseText[phase.phaseTypeId - 1]}
          />
        ),
      };
    }
  );

  function isPhaseDataInconsistent(): boolean {
    const currentMappedDualisPhase =
      PHASE_NAME_MAPPING[request?.projectPhase] ?? "";
    const currentDigixPhase =
      digiXProject.phases.find((phase) => phase.isCurrent)?.name ?? "";
    const currentMappedDigixPhase = PHASE_NAME_MAPPING[currentDigixPhase] ?? "";

    return currentMappedDualisPhase !== currentMappedDigixPhase;
  }

  /**
   * onClick method of the expand / collapse all button.
   * it sets the active keys to either all keys or none and changes the state to all collapse or none collapsed
   */
  function onClick(): void {
    const newActiveKeys: string[] = allCollapsed
      ? digiXProject?.phases?.map((_: DigixPhase, index: number) =>
          index.toString()
        )
      : [];
    setActiveKeys(newActiveKeys);
    setAllCollapsed(!allCollapsed);
  }

  /**
   * onChange method of the collapse. Sets the active keys to all keys that are expanded
   * @param {string[]} keys contains all keys that are expanded
   */
  function onChange(keys: string[]): void {
    setActiveKeys(keys);
  }

  return (
    <>
      {isPhaseDataInconsistent() && (
        <NotificationBanner
          type={NOTIFICATION_TYPE.WARNING}
          message={
            "Phase data is inconsistent between Dualis and DigiX, DigiX data being shown here is the latest data."
          }
        />
      )}
      <Row gutter={[16, 16]} className="digix-details-tab">
        <Col span={8}>
          <Card className="info-card">
            <Flex vertical gap={10}>
              <CardLabel label="Digi X Project Name" showToolTip={false}>
                {digiXProject?.name}
              </CardLabel>
              <CardLabel label="Category" showToolTip={false}>
                {digiXProject?.category || notAvailableText}
              </CardLabel>
              <CardLabel label="MTP Category" showToolTip={false}>
                {digiXProject?.mtpCategory || notAvailableText}
              </CardLabel>
              <CardLabel label="Next Auction" showToolTip={false}>
                {digiXProject?.nextAuction || notAvailableText}
              </CardLabel>
              <CardLabel label="Gross Capacity" showToolTip={false}>
                {digiXProject?.grossCapacity || notAvailableText}
              </CardLabel>
            </Flex>
          </Card>
        </Col>
        <Col span={15}>
          <div className="digix-details-tab__phases">
            <Button
              className="expand-all-button"
              onClick={onClick}
              type="link"
              icon={allCollapsed ? <DownOutlined /> : <UpOutlined />}
              iconPosition="end"
            >
              {allCollapsed ? "EXPAND ALL" : "COLLAPSE ALL"}
            </Button>
            <Collapse
              items={items}
              activeKey={activeKeys}
              expandIconPosition="end"
              ghost
              onChange={onChange}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default DigiXDetailsTab;
